//@ts-nocheck
import { ArrowBackIcon, InfoIcon, QuestionIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React from "react";
import { array, object, string } from "yup";
import { formatCurrency, formatPrice } from "../../../utils/functions";

const placeholders = {
  hourlyRate: {
    label: "Enter hourly rate per student",
    placeholder: "Type a price",
  },
  confirmation: {
    label: "Confirm price change",
    placeholder: "Confirm price change",
  },
};

const validationSchema = object().shape({
  hourlyRate: string().required("Enter a price above"),
  confirmation: array()
    .of(string())
    .min(2, "Please confirm price change")
    .required("Please confirm price change"),
});

const commonProps = ({ name }, { touched, errors }) => ({
  ...placeholders[name],
  isInvalid: Boolean(touched[name] && errors[name]),
  errorMessage: errors[name],
});

export const EditHourlyPrice: React.FC<{
  initialValues: { hourlyRate: string };
  onBackClick: () => void;
  howPriceWorksClick?: (step: string) => void;
  priceSuggestion?: {
    minimum: string | number;
    maximum: string | number;
    recommended: string | number;
  };
  earningPercentage?: number;
  withholdingTax?: number;
  isLoading?: boolean;
  currency: string;
  subject?: string;
  onSubmit: (values: any) => void;
}> = observer(
  ({
    onBackClick,
    initialValues,
    earningPercentage,
    howPriceWorksClick,
    priceSuggestion,
    withholdingTax,
    currency,
    subject,
    isLoading,
    onSubmit,
  }) => {
    const [showBreakdown, setShowBreakdown] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    return (
      <Box className="edit-hourly-price">
        <Button
          mb={6}
          onClick={onBackClick}
          leftIcon={<ArrowBackIcon />}
          variant="link"
          colorScheme="blue"
        >
          Back
        </Button>

        <Stack pb={6}>
          <Text fontSize="3xl" lineHeight="shorter" fontWeight="bold" as="h1">
            Edit pricing
          </Text>
          <Text mt={1} color="gray.700">
            You can change your price at anytime and it will apply to new
            clients
          </Text>
        </Stack>

        <Formik
          initialValues={{
            hourlyRate: initialValues.hourlyRate,
            confirmation: [],
          }}
          onSubmit={(values) => {
            delete values.confirmation;
            onSubmit(values);
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values }) => {
            const commission = earningPercentage / 100;
            const lessonPrice = Number(values.hourlyRate) * 24 * commission;
            const earnings = formatPrice(
              Number(values.hourlyRate) * 24 * commission,
            );
            const taxValue = formatPrice((lessonPrice * withholdingTax) / 100);

            return (
              <Form>
                <Stack mb={8} spacing={8}>
                  <Field
                    name="hourlyRate"
                    render={({ field, form }) => (
                      <Stack spacing="6">
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <FormLabel>
                            {commonProps(field, form).label}
                          </FormLabel>
                          <Box
                            borderWidth="2xs"
                            borderColor="neutral.500"
                            rounded="md"
                            overflow="hidden"
                          >
                            <Flex>
                              <Input
                                height="3.5rem"
                                size="lg"
                                flex="5"
                                opacity="1"
                                rounded="none"
                                border="none"
                                fontSize="xl"
                                fontWeight={field.value ? "medium" : "normal"}
                                borderColor="neutral.700"
                                {...field}
                                {...commonProps(field, form)}
                                name={field.name}
                                value={formatPrice(field.value)}
                                onBlur={() => setShowConfirmation(true)}
                                onChange={({ target }) => {
                                  form.setFieldValue(
                                    field.name,
                                    formatCurrency(target.value),
                                  );
                                  form.setFieldValue("confirmation", []);
                                  setShowConfirmation(false);
                                }}
                              />

                              <Button
                                flex={1}
                                bg="white"
                                shadow="none"
                                color="blue.500"
                                borderLeftWidth="2xs"
                                borderColor="neutral.500"
                                roundedLeft="none"
                                roundedBottomRight="none"
                                height="3.5rem"
                                onClick={() => {
                                  setShowConfirmation(true);
                                  setShowBreakdown(false);
                                }}
                              >
                                Done
                              </Button>
                            </Flex>

                            <Stack
                              px={4}
                              py={3}
                              bg="neutral.200"
                              borderTopWidth="2xs"
                              borderColor="neutral.500"
                            >
                              <HStack justifyContent="space-between">
                                <Box>
                                  <Text fontWeight="light" fontSize="xs">
                                    Suggestion
                                  </Text>
                                  <HStack>
                                    <Text>
                                      {formatPrice(priceSuggestion.recommended)}{" "}
                                      /hr
                                    </Text>
                                    <QuestionIcon
                                      cursor="pointer"
                                      onClick={() =>
                                        setShowBreakdown(!showBreakdown)
                                      }
                                    />
                                  </HStack>
                                </Box>

                                <Button
                                  bg="black"
                                  size="sm"
                                  color="white"
                                  rounded="md"
                                  onClick={() => {
                                    form.setFieldValue(
                                      field.name,
                                      priceSuggestion.recommended,
                                    );
                                    setShowConfirmation(true);
                                    setShowBreakdown(false);
                                  }}
                                >
                                  Use this
                                </Button>
                              </HStack>

                              <Collapse in={showBreakdown}>
                                <Box pb={2}>
                                  Your price often influences clients' decision
                                  to hire you or not.
                                </Box>

                                <Box pb={2}>
                                  To increase your chances of getting bookings,
                                  consider charging between{" "}
                                  {formatPrice(priceSuggestion.minimum)} -{" "}
                                  {formatPrice(priceSuggestion.maximum)} per
                                  hour based on market rates for similar tutors
                                  around your location.
                                </Box>

                                <Text>
                                  This is only a suggestion. You can charge any
                                  price that best reflects your value.
                                </Text>

                                <Button
                                  my={5}
                                  variant="link"
                                  colorScheme="blue"
                                  onClick={() =>
                                    howPriceWorksClick("Edit-pricing")
                                  }
                                >
                                  Learn how pricing works
                                </Button>
                              </Collapse>
                            </Stack>
                          </Box>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      </Stack>
                    )}
                  />

                  {showConfirmation && values.hourlyRate && (
                    <Field
                      name="confirmation"
                      render={({ field, form }) => (
                        <Stack
                          p={[5, 6]}
                          spacing={6}
                          borderWidth="2xs"
                          borderColor="neutral.500"
                          rounded="md"
                        >
                          <Stack>
                            <Box mb={2}>
                              <InfoIcon fontSize="2xl" color="blue.500" />
                            </Box>
                            <Text fontSize="lg" fontWeight="semibold">
                              Confirm price change
                            </Text>

                            <Text>
                              By setting this price, it means clients who wish
                              to book 2-hour lessons, thrice a week, for
                              example, will be charged{" "}
                              {formatPrice(Number(values.hourlyRate) * 24)} per
                              month.
                            </Text>

                            <Text>
                              In this example, you'll earn {earnings} (
                              {earningPercentage}%), and pay {taxValue} as tax.
                            </Text>

                            <Text>Do you wish to set this price?</Text>

                            <FormControl
                              isInvalid={commonProps(field, form).isInvalid}
                            >
                              <CheckboxGroup
                                {...field}
                                {...commonProps(field, form)}
                                name={field.name}
                                onChange={(value) => {
                                  form.setFieldValue(field.name, value);
                                }}
                              >
                                <Stack>
                                  <Checkbox
                                    value="fair-price"
                                    size="lg"
                                    isInvalid={
                                      commonProps(field, form).isInvalid
                                    }
                                    borderColor="neutral.500"
                                  >
                                    <Text fontSize="md">
                                      Yes, I think it's a fair price
                                    </Text>
                                  </Checkbox>
                                  <Checkbox
                                    value="tuteria-commission"
                                    size="lg"
                                    isInvalid={
                                      commonProps(field, form).isInvalid
                                    }
                                    borderColor="neutral.500"
                                  >
                                    <Text fontSize="md">
                                      I will attract clients at this price
                                    </Text>
                                  </Checkbox>
                                </Stack>
                              </CheckboxGroup>

                              <FormErrorMessage>
                                {commonProps(field, form).errorMessage}
                              </FormErrorMessage>
                            </FormControl>
                          </Stack>
                        </Stack>
                      )}
                    />
                  )}

                  {showConfirmation && values.hourlyRate && (
                    <Button
                      colorScheme="blue"
                      size="lg"
                      isLoading={isLoading}
                      loadingText="Saving"
                      onClick={() => handleSubmit()}
                    >
                      Save new price
                    </Button>
                  )}
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Box>
    );
  },
);
